import { tr } from '@/lib/i18n-core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post, put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { useToast } from '@/components/ui/toast';
import { printSlip } from '../FrontDeskDialogs';
import type { SlipData } from '../token-slip';

export function useAppointmentActions() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['calendar'] });
    void queryClient.invalidateQueries({ queryKey: ['appointments'] });
    void queryClient.invalidateQueries({ queryKey: ['queue'] });
  };

  const arrive = useMutation({
    mutationFn: (id: string) => post<{ slip: SlipData }>(`/appointments/${id}/arrive`, {}),
    onSuccess: async (result) => {
      toast.success(tr('opd', 'arrived'), result.slip.tokenNo ? tr('opd', 'token_x', { tokenNo: result.slip.tokenNo }) : undefined);
      refresh();
      await printSlip(result.slip.appointmentId, result.slip);
    },
    onError: (error) => toast.error(tr('opd', 'could_not_mark_the_arrival'), describeError(error)),
  });

  const changeStatus = useMutation({
    mutationFn: ({ id, next }: { id: string; next: string }) =>
      put<{ cancelledInvoices?: number; refundNeeded?: boolean }>(`/appointments/${id}/status`, { status: next }),
    onSuccess: (result, variables) => {
      const what = variables.next === 'no_show' ? tr('opd', 'marked_as_not_seen') : tr('opd', 'appointment_updated');
      if (result.refundNeeded) {
        toast.warning(tr('opd', 'x_a_refund_is_due', { what }), tr('opd', 'the_fee_was_already_paid_the_appointment_is'));
      } else if (result.cancelledInvoices) {
        toast.success(what, tr('opd', 'x_unpaid_fee_invoice_s_were_cancelled_with_it', { cancelledInvoices: result.cancelledInvoices }));
      } else {
        toast.success(what);
      }
      refresh();
    },
    onError: (error) => toast.error(tr('opd', 'could_not_update_the_appointment'), describeError(error)),
  });

  return { arrive, changeStatus, refresh };
}
